import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, makeStyles, Toolbar } from "@material-ui/core";
import { Typography, Alert } from "@mui/material";
import { useIntl } from "react-intl";
import ProductList from "../ProductList/ProductList";
import {
  isFetchingSortedProducts,
  selectContentSlots,
  selectIsProductListFetching,
  selectIsProductListsFetched,
  selectIsSequenceLoading,
  selectIsSequenceResponseFetched,
  selectProductListsFetched,
  selectTotal,
  selectProductListSequenceMode,
  selectProductListEditSequence,
  selectIsProductIdsFetched,
  selectSelectedItems,
  selectIsPreviewMode,
  selectCurrentPage,
} from "../../store/product-list/ProductListSelectors";
import ProductListActionBtn from "../ProductListActionBtn";
import ProductListSwitch from "../ProductListSwitch";
import ProductListGroupBtn from "../ProductListGroupBtn";
import Box from "@material-ui/core/Box";
import ViewContextProvider from "../../context/view-context";
import HeatMapModal from "../heat-map-modal/HeatMapModal";
import ItemDragLayer from "../ItemDragLayer";
import BreadcrumbSequence from "../BreadcrumbSequence";
import {
  selectIsFetchedBySearch,
  selectProductColors,
  selectProductListBySearch,
  selectProducts,
  selectRecentlyAddedNewProducts,
} from "../../store/product/ProductSelectors";
import {
  fetchPageColorBadgeData,
  resetProductSequence,
  resetProgressCache,
  setCurrentPage,
  resetPreview,
  clearPendingScrollTarget,
  cancelProductListFetch,
} from "../../store/product-list/ProductListActions";
import { selectPendingScrollTarget } from "../../store/product-list/ProductListSelectors";
import { getAdjustedIndexWithContentSlots } from "../../utils/ProductUtil";
import {
  resetSequenceByBoostBury,
  updateRecentlyAddedProductsAfterSave,
} from "../../store/product/ProductActions";
import { useQuery } from "../../hooks/useQueryParams";
import { selectIsVerifyingProductCodes } from "../../store/clipboard/ClipBoardSelectors";
import {
  getTopCategoryProductCount,
  selectCurrentCategory,
} from "store/category/CategorySelectors";
import ProductListPagination from "../ProductListPagination";
import AppEnv from "utils/AppEnv";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "store/store-list/StoreListSelectors";
import SelectProducts from "../SelectProducts/SelectProducts";
import { fetchProductDetailsPerPage } from "../../store/product-list/ProductListActions";
import { selectIsCategoryProductsV2Available } from "store/adapter-config/AdapterConfigSelectors";
import { selectisUpdatingLeadingColorForMultipleProducts } from "store/color-management/ColorManagementSelectors";

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "40px",
    height: "auto",
    padding: 0,
    gap: "12px",
  },
  breadcrumbSection: {
    flex: "0 0 auto",
  },
  previewAlert: {
    flex: "0 1 auto",
    width: "fit-content",
    minWidth: "150px",
    marginLeft: "3%",
    padding: "0px 8px !important",
    "& .MuiAlert-icon": {
      marginRight: "8px",
      padding: 0,
      alignSelf: "flex-start",
      marginTop: "2px",
    },
    "& .MuiAlert-message": {
      display: "block",
      padding: 0,
    },
  },
  exitLink: {
    cursor: "pointer",
    fontWeight: 600,
    textDecoration: "underline",
  },
  actionBtnSection: {
    flex: "0 0 auto",
  },
  productCount: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "20px",
    padding: "2px 15px 0px 0px",
    "& > div": {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      "& > :nth-child(1)": {
        flex: "0 0 auto",
      },
      "& > :nth-child(2)": {
        position: "absolute",
        left: "53%",
        transform: "translateX(-50%)",
      },
    },
  },
  overlay: {
    position: "fixed",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.2)",
    zIndex: 99999,
    cursor: "pointer",
  },
  loaderClass: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  loaderText: {
    marginRight: 20,
    fontSize: "20px",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#fff",
    padding: "15px",
    borderRadius: "4px",
  },
});

interface Props {}

const ProductListContainer: React.FC<Props> = () => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const query = useQuery();
  const products = useSelector(selectProductListsFetched);
  const editProducts = useSelector(selectProductListEditSequence);
  const productsBySearch = useSelector(selectProductListBySearch);
  const isFetchedBySearch = useSelector(selectIsFetchedBySearch);
  const totalProductsInList = useSelector(selectTotal);
  const totalProductsInCategory = useSelector(getTopCategoryProductCount);
  const sequenceMode = useSelector(selectProductListSequenceMode);
  const isSequenceLoading = useSelector(selectIsSequenceLoading);
  const recentlyAddedProducts = useSelector(selectRecentlyAddedNewProducts);
  const isFetchingSortedProductsList = useSelector(isFetchingSortedProducts);
  const isUpdatingLeadingColorForMultipleProducts = useSelector(
    selectisUpdatingLeadingColorForMultipleProducts,
  );
  const isSequenceResponseFetched = useSelector(
    selectIsSequenceResponseFetched,
  );
  const categoryPathQuery = query.get("categoryPath") ?? "";
  const isProductListsFetched = useSelector(selectIsProductListsFetched);
  const isProductListFetching = useSelector(selectIsProductListFetching);
  const isProductIdsFetched = useSelector(selectIsProductIdsFetched);
  const isPreviewMode = useSelector(selectIsPreviewMode);
  const currentPageFromState = useSelector(selectCurrentPage);

  const isVerifyingProductCodes = useSelector(selectIsVerifyingProductCodes);
  const contentSlots = useSelector(selectContentSlots);
  const selectedItems = useSelector(selectSelectedItems);
  const catalogId = useSelector(selectCurrentCatalogId);
  const storeId = useSelector(selectCurrentStoreId);
  const categoryId = useSelector(selectCurrentCategory);
  const localeCode = useSelector(selectCurrentLocale);
  const allProductsData = useSelector(selectProducts);
  const allProductColorsData = useSelector(selectProductColors);
  const isCategoryProductsV2Available = useSelector(
    selectIsCategoryProductsV2Available,
  );

  const [page, setPage] = useState(1);
  const pageSize = AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24;
  const [searchText, setSearchText] = useState<string>("");
  const [currentProductId, setCurrentProductId] = useState<string | undefined>(
    undefined,
  );
  const productListRef = useRef<{
    scrollToProductIndex: (productIndex: number) => void;
  }>(null);

  const getTotalItems = useCallback(() => {
    const totalItems = isFetchedBySearch
      ? productsBySearch
      : sequenceMode === 1
        ? products
        : editProducts;

    return totalItems;
  }, [
    isFetchedBySearch,
    productsBySearch,
    sequenceMode,
    products,
    editProducts,
  ]);

  const getPageItems = useCallback(
    (newPage?: number) => {
      if (!newPage) {
        newPage = page;
      }
      const allProducts = getTotalItems();

      // Calculate total items including content slots
      const totalItems = allProducts.length + (contentSlots?.length || 0);

      // Calculate the start and end positions for this page
      const start = (newPage - 1) * pageSize;
      const end = Math.min(start + pageSize, totalItems);

      // Get content slots that should appear in this page
      const pageSlots =
        contentSlots?.filter((slot) => {
          const position = slot.position - 1; // Convert to 0-based index
          return position >= start && position < end;
        }) || [];

      // Calculate how many product slots are available on this page
      const availableProductSlots = pageSize - pageSlots.length;

      // Calculate the product range for this page
      let productStart = start;
      let productEnd = start + availableProductSlots;

      // Adjust for content slots in previous pages
      const previousSlots =
        contentSlots?.filter((slot) => {
          const position = slot.position - 1;
          return position < start;
        }) || [];

      // Adjust the product range based on previous content slots
      productStart -= previousSlots.length;
      productEnd -= previousSlots.length;

      // Ensure we don't exceed the total number of products
      productEnd = Math.min(productEnd, allProducts.length);
      productStart = Math.min(productStart, allProducts.length);

      // Get the products for this page
      const pageProducts = allProducts.slice(productStart, productEnd);

      return pageProducts;
    },
    [page, pageSize, contentSlots, getTotalItems],
  );

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
    isFromPagination: boolean,
  ) => {
    setPage(newPage);
    dispatch(setCurrentPage(newPage));
    const pageProducts = getPageItems(newPage);
    const currentPageProductIds = pageProducts.map(
      (product) => product.productId,
    );
    const currentPageProducts = Object.values(allProductsData).filter(
      (product) => currentPageProductIds.includes(product.productId),
    );

    if (isFromPagination && currentPageProducts.length > 0) {
      // Filter out only the products that are not loaded
      const productsNotLoaded = currentPageProductIds.filter((productId) => {
        const product = allProductsData[productId];
        return !product || !product.productDetailsLoaded;
      });
      const colorProductsNotLoaded = currentPageProductIds.filter(
        (productId) => {
          const hasColors = allProductsData[productId].hasColors;
          if (hasColors) {
            const product = allProductColorsData[productId];
            return !product || !product.productColorDetailsLoaded;
          }
          return false;
        },
      );
      dispatch(resetProgressCache());
      // Only proceed if there are missing products to load
      if (productsNotLoaded.length > 0 && isCategoryProductsV2Available) {
        dispatch(
          fetchProductDetailsPerPage({
            page: newPage,
            catalogId: catalogId,
            storeId: storeId,
            categoryId: categoryId,
            localeCode: localeCode,
            productIds: productsNotLoaded, // Only load missing products
            pageSize: pageSize,
          }),
        );
      } else if (colorProductsNotLoaded.length > 0) {
        const missingColorProducts = currentPageProducts.filter((product) =>
          colorProductsNotLoaded.includes(product.productId),
        );

        dispatch(
          fetchPageColorBadgeData({
            page: newPage,
            catalogId: catalogId,
            storeId: storeId ?? "",
            categoryId: categoryId,
            localeCode: localeCode,
            products: missingColorProducts, // Only load missing products
            storeListIds: [],
          }),
        );
      }
    }
  };

  const handleSearchChange = (value: string, productId?: string) => {
    setSearchText(value);
    setCurrentProductId(productId);
  };

  const pendingScrollTarget = useSelector(selectPendingScrollTarget);
  useEffect(() => {
    if (!pendingScrollTarget) return;
    const sequences = getTotalItems();
    const productIndex = sequences.findIndex(
      (seq) => seq.productId === pendingScrollTarget.productId,
    );
    if (productIndex < 0) {
      dispatch(clearPendingScrollTarget());
      return;
    }
    const adjustedGlobalIndex = getAdjustedIndexWithContentSlots(
      productIndex,
      contentSlots,
    );
    const newPage = Math.ceil((adjustedGlobalIndex + 1) / pageSize);

    if (newPage === page) {
      productListRef?.current?.scrollToProductIndex(adjustedGlobalIndex);
      dispatch(clearPendingScrollTarget());
    } else {
      handlePageChange(
        {} as React.ChangeEvent<unknown>,
        newPage,
        true,
      );
      setTimeout(() => {
        productListRef?.current?.scrollToProductIndex(adjustedGlobalIndex);
        dispatch(clearPendingScrollTarget());
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingScrollTarget?.nonce]);

  // Reset page when category changes
  useEffect(() => {
    setPage(1);
  }, [categoryPathQuery]);

  // Stop in-flight category-product fetching when this container
  // unmounts (e.g. user navigates from Catalog to Dashboard or Business Rules). 
  useEffect(() => {
    return () => {
      dispatch(cancelProductListFetch());
    };
  }, [dispatch]);

  useEffect(() => {
    if (currentPageFromState !== page) {
      setPage(currentPageFromState);
    }
  }, [currentPageFromState, page]);

  // Handle page redirection when last item is deleted
  useEffect(() => {
    const totalProducts = getTotalItems().length;
    const totalContentSlots = contentSlots?.length || 0;
    const totalItems = totalProducts + totalContentSlots;
    const totalPages = Math.ceil(totalItems / pageSize);

    // If current page is greater than total pages and not on page 1,
    // redirect to the previous page
    if (page > totalPages && page > 1) {
      setPage(page - 1);
    }
  }, [getTotalItems, page, pageSize, contentSlots]);

  useEffect(() => {
    if (isSequenceLoading) {
      const categoryIds = categoryPathQuery.split(";");
      const categoryId = categoryIds[categoryIds.length - 1];
      if (recentlyAddedProducts.length) {
        const filteredData = recentlyAddedProducts.filter((data, index) => {
          if (Object.keys(data)[0] === categoryId) {
            // eslint-disable-next-line array-callback-return
            return;
          }
          return data;
        });
        dispatch(updateRecentlyAddedProductsAfterSave(filteredData));
      }

      dispatch(resetProductSequence());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSequenceLoading]);

  useEffect(() => {
    if (isSequenceResponseFetched) {
      dispatch(resetSequenceByBoostBury());
    }
  }, [isSequenceResponseFetched, dispatch]);

  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    // Set initial loading to false after the first page load is complete
    if (products.length > 0) {
      setInitialLoading(false);
    }
  }, [products.length, totalProductsInCategory]);

  const pageItems = getPageItems();

  const handlePreviewModeExit = () => {
    dispatch(resetPreview());
  };

  const productPanel = (
    <>
      <Toolbar className={classes.toolbar}>
        <div className={classes.breadcrumbSection}>
          <BreadcrumbSequence />
        </div>
        {isPreviewMode && (
          <Alert severity="success" className={classes.previewAlert}>
            {intl.formatMessage({
              id: "previewModeAlert.message",
              defaultMessage:
                "You are currently in Preview mode. Grid positioning, sorting, and clipboard actions are disabled.",
            })}{" "}
            <span>
              <span
                role="button"
                tabIndex={0}
                className={classes.exitLink}
                onClick={handlePreviewModeExit}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handlePreviewModeExit();
                  }
                }}
              >
                {intl.formatMessage({
                  id: "previewModeAlert.exit",
                  defaultMessage: "Exit",
                })}
              </span>{" "}
              {intl.formatMessage({
                id: "previewModeAlert.toReturn",
                defaultMessage: "to return to Working mode.",
              })}
            </span>
          </Alert>
        )}
        <div className={classes.actionBtnSection}>
          <ProductListActionBtn />
        </div>
      </Toolbar>
      <Toolbar className={classes.toolbar}>
        <ProductListGroupBtn
          products={products}
          currentPageProductIds={
            pageItems?.map((product) => product.productId) || []
          }
        />
        <ProductListSwitch />
      </Toolbar>
      <Toolbar className={classes.productCount}>
        {!initialLoading && isProductIdsFetched && (
          <div>
            <Typography variant="heading3_regular" noWrap>
              {isPreviewMode
                ? intl.formatMessage(
                    {
                      id: "productListSwitch.previewProductsText",
                      defaultMessage:
                        "{totalProducts} of {totalProductsInCategory} products",
                    },
                    {
                      totalProducts: products.length ?? "?",
                      totalProductsInCategory: totalProductsInCategory ?? "?",
                    },
                  )
                : totalProductsInCategory !== undefined &&
                    totalProductsInCategory !== null
                  ? intl.formatMessage(
                      {
                        id: "productListSwitch.totalProductsText",
                        defaultMessage: "{totalProducts} products",
                      },
                      {
                        totalProducts: totalProductsInCategory,
                      },
                    )
                  : intl.formatMessage(
                      {
                        id: "productListSwitch.publishedProductsOnlyText",
                        defaultMessage:
                          "{publishedProducts} published products",
                      },
                      {
                        publishedProducts: totalProductsInList ?? "?",
                      },
                    )}
            </Typography>
            {selectedItems.length > 0 && (
              <SelectProducts selectedItems={selectedItems} />
            )}
          </div>
        )}
      </Toolbar>
      {isProductIdsFetched && products.length > 0 && (
        <ProductListPagination
          page={page}
          onPageChange={handlePageChange}
          onSearchChange={handleSearchChange}
          pageSize={pageSize}
          productListRef={productListRef}
          contentSlots={contentSlots}
        />
      )}
    </>
  );

  const renderPartNumberVerificationLoader = () => (
    <Box className={classes.overlay}>
      <div className={classes.loaderContainer}>
        <Box className={classes.loaderText}>
          {intl.formatMessage({
            id: "clipBoard.validatingPartNumbers",
            defaultMessage: "Validating Part Numbers...",
          })}
        </Box>
        <Box>
          <CircularProgress size={25} />
        </Box>
      </div>
    </Box>
  );

  return (
    <ViewContextProvider>
      {/*
        HeatMapModal is rendered inside the ViewContextProvider so the
        planogram receives the same column count the user has selected for
        the main grid (ProductListViewSelect updates this context).
      */}
      <HeatMapModal />
      {productPanel}
      {isVerifyingProductCodes && renderPartNumberVerificationLoader()}
      {isProductListsFetched &&
      !isProductListFetching &&
      products.length === 0 ? (
        <Box position="absolute" top="50%" left="50%">
          <Typography variant="h5">
            {intl.formatMessage({
              id: "productListSwitch.noProductsFoundText",
              defaultMessage: "No Products Available",
            })}
          </Typography>
        </Box>
      ) : (
        <>
          <ItemDragLayer />
          {(isFetchingSortedProductsList ||
            isUpdatingLeadingColorForMultipleProducts) && (
            <div className={classes.overlay}>
              <CircularProgress />
            </div>
          )}
          {isVerifyingProductCodes && renderPartNumberVerificationLoader()}
          <ProductList
            ref={productListRef}
            products={pageItems}
            contentSlots={contentSlots}
            pageStart={(page - 1) * pageSize}
            pageEnd={page * pageSize}
            totalProducts={getTotalItems()}
            searchText={searchText}
            currentProductId={currentProductId}
            pageSize={pageSize}
          />
        </>
      )}
    </ViewContextProvider>
  );
};

export default React.memo(ProductListContainer);
